import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Container from '@grnhse/seedling/lib/birch/components/container';
import BorderlessContainer from 'shared/components/birch/borderless_container';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import {
  InlineMarkdown,
  Link,
  Label,
  SecondaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import DefinitelyNotIcon from '@grnhse/seedling/lib/birch/icons/definitely_not';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import ForwardIcon from '@grnhse/seedling/lib/birch/icons/forward';
import { PrimaryTag } from '@grnhse/seedling/lib/birch/components/tags';
import DocumentCard from './document_card';
import ScorecardRatingPills from './scorecard_rating_pills';
import ScorecardSummary from './scorecard_summary';
import type { ApplicationHistoryItem, Document } from './types';
import { formatShortDate, formatLongDate } from 'talent_search/lib/utils';
import { withLocalePrefix } from 'shared/utils/translation';
import noop from 'lodash/noop';
import { scorecardSummaryText } from './scorecard_summary_text';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

const t = withLocalePrefix('shared.application_history');

type Props = {
  application: ApplicationHistoryItem;
  userDateFormat: string;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

const RowDetail = styled(SecondaryBody)`
  white-space: nowrap;
`;

const LabelGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: ${Tokens.spacing['3']};
  row-gap: ${Tokens.spacing['0.5']};

  > *:nth-child(odd) {
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
    min-width: max-content;
  }
`;

const StyledSecondaryBody = styled(SecondaryBody)<{ $color?: string }>`
  && {
    ${({ $color }) => $color && `color: ${$color};`}
  }
`;

const MediumLink = styled(Link)`
  &&&& {
    font-weight: 500;
  }
`;

const TertiaryLink = styled(Link)`
  &&&& {
    font-size: 0.75rem;
  }
`;

const ApplicationHistoryCard = ({
  application,
  userDateFormat,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
}: Props) => {
  const {
    prospect = false,
    hiringPlanName,
    url,
    hiringPlanUrl,
    applicationStageName,
    daysInStage,
    appliedDate,
    source,
    recruiter,
    rejectedOn,
    rejectionReason,
    rejectionNote,
    hiredOn,
    positiveRatingPercentage,
    scorecardDetails,
    scorecardRatingsSummary,
    scorecardSummary,
    attachments = [],
    prospectiveJobs = [],
    prospectPoolName,
    prospectStageName,
    prospectOwner,
  } = application;

  const isRejected = rejectedOn !== null;
  const isHired = hiredOn !== null;

  const title = prospect
    ? prospectPoolName
      ? t('prospect_in_pool', { pool: prospectPoolName })
      : t('prospect')
    : hiringPlanName;
  const titleUrl = prospect ? url : hiringPlanUrl ?? url;

  const stageName = prospect ? prospectStageName ?? null : applicationStageName;
  const stageDisplay = stageName
    ? !prospect && daysInStage !== null && daysInStage > 0
      ? t('stage_with_days', {
          stage: stageName,
          days: daysInStage,
        })
      : stageName
    : null;

  const owner = prospect ? prospectOwner : recruiter;

  const interviewRatingsLabel = scorecardSummaryText(positiveRatingPercentage);
  const hasScorecards = positiveRatingPercentage !== null;

  return (
    <Container>
      <Stack spacing={2}>
        <Stack spacing={0}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <MediumLink href={titleUrl} target="_blank">
              {title}
            </MediumLink>
            {application.matchingApplication && (
              <PrimaryTag text={t('matching_application')} />
            )}
          </Stack>
        </Stack>

        {isRejected && (
          <BorderlessContainer filled>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DefinitelyNotIcon size="sm" color={FullPalette.POPPY_600} />
                <StyledSecondaryBody $color={FullPalette.POPPY_600}>
                  <InlineMarkdown
                    text={
                      rejectedOn
                        ? t('rejected_on', {
                            date: formatShortDate(rejectedOn, userDateFormat),
                          })
                        : t('rejected')
                    }
                  />
                </StyledSecondaryBody>
              </Stack>
              <Stack spacing={0.5}>
                {rejectionReason && (
                  <Stack spacing={0.5}>
                    <Label>{t('rejection_reason_label')}</Label>
                    <SecondaryBody>{rejectionReason}</SecondaryBody>
                  </Stack>
                )}
                {rejectionNote && (
                  <Stack spacing={0.5}>
                    <Label>{t('rejection_note_label')}</Label>
                    <SecondaryBody>{rejectionNote}</SecondaryBody>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </BorderlessContainer>
        )}

        {isHired && (
          <BorderlessContainer filled>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon size="sm" color={FullPalette.GREEN_700} />
                <StyledSecondaryBody $color={FullPalette.GREEN_700}>
                  <InlineMarkdown
                    text={
                      hiredOn
                        ? t('hired_on', {
                            date: formatShortDate(hiredOn, userDateFormat),
                          })
                        : t('hired')
                    }
                  />
                </StyledSecondaryBody>
              </Stack>
            </Stack>
          </BorderlessContainer>
        )}

        <LabelGrid>
          {prospect && prospectiveJobs.length > 0 && (
            <>
              <Label>{t('prospective_jobs_label')}</Label>
              <SecondaryBody>
                {prospectiveJobs.map((job, index) => (
                  <React.Fragment key={job.id}>
                    {index > 0 && ', '}
                    <Link href={job.url} target="_blank">
                      {job.name}
                    </Link>
                  </React.Fragment>
                ))}
              </SecondaryBody>
            </>
          )}
          {stageDisplay && (
            <>
              <Label>{t('stage_label')}</Label>
              <RowDetail>{stageDisplay}</RowDetail>
            </>
          )}
          {appliedDate && (
            <>
              <Label>{prospect ? t('added_label') : t('applied_label')}</Label>
              <RowDetail>
                {formatLongDate(appliedDate, userDateFormat)}
              </RowDetail>
            </>
          )}
          {source && (
            <>
              <Label>{t('source_label')}</Label>
              <RowDetail>{source}</RowDetail>
            </>
          )}
          {owner && (
            <>
              <Label>
                {prospect ? t('prospect_owner_label') : t('recruiter_label')}
              </Label>
              <RowDetail>{owner}</RowDetail>
            </>
          )}
        </LabelGrid>

        {!prospect && (
          <>
            <Divider />

            <Stack spacing={1}>
              <Stack direction="row" justifyContent="flex-start" spacing={1}>
                <SecondaryBody>{interviewRatingsLabel}</SecondaryBody>
                {hasScorecards && (
                  <TertiaryLink href={`${url}#scorecard`} target="_blank">
                    {t('scorecards_link')}
                  </TertiaryLink>
                )}
              </Stack>
              {hasScorecards && (
                <ScorecardRatingPills
                  summary={scorecardRatingsSummary}
                  scorecardDetails={scorecardDetails}
                />
              )}
              {scorecardSummary && (
                <ScorecardSummary summary={scorecardSummary} />
              )}
            </Stack>
          </>
        )}

        <Divider />

        {attachments.length > 0 && (
          <Stack spacing={1}>
            <Label>{t('documents')}</Label>
            {attachments.map((doc, index) => (
              <DocumentCard
                key={doc.id ?? index}
                document={doc}
                selected={!!activeDocument && doc.id === activeDocument.id}
                canDownloadCandidateData={canDownloadCandidateData}
                isRediscovery
                onClick={
                  doc.previewable !== false && onOpenDocument
                    ? () => onOpenDocument(doc)
                    : undefined
                }
                onPreview={
                  doc.previewable !== false && onOpenDocument
                    ? () => onOpenDocument(doc)
                    : undefined
                }
              />
            ))}
          </Stack>
        )}

        <Stack alignItems="flex-start">
          <Link href={url} target="_blank">
            <SecondaryButton
              onClick={() => noop}
              icon={() => (
                <ForwardIcon size="sm" color={FullPalette.GREEN_700} />
              )}
              iconPosition="right"
              size="md"
            >
              {prospect ? t('go_to_profile') : t('go_to_application')}
            </SecondaryButton>
          </Link>
        </Stack>
      </Stack>
    </Container>
  );
};

export default ApplicationHistoryCard;
